import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';
import './SearchableSelect.css';

const SearchableSelect = ({
  value,
  options = [],
  onChange,
  placeholder = 'Search and select...',
  disabled = false,
  className = '',
  error = false,
  name,
  getOptionLabel = (option) => option.name || option.label || option,
  getOptionValue = (option) => option.id || option.value || option,
  loading = false,
  noOptionsText = 'No options found'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const optionsRef = useRef(null);

  // Find selected option
  const selectedOption = useMemo(() => {
    return options.find(option => getOptionValue(option) === value);
  }, [options, value, getOptionValue]);

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    
    const query = searchQuery.toLowerCase();
    return options.filter(option => 
      getOptionLabel(option).toLowerCase().includes(query)
    );
  }, [options, searchQuery, getOptionLabel]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setHighlightedIndex(prev => 
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setHighlightedIndex(prev => 
            prev > 0 ? prev - 1 : filteredOptions.length - 1
          );
          break;
        case 'Enter':
          event.preventDefault();
          if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
            handleSelect(filteredOptions[highlightedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setSearchQuery('');
          setHighlightedIndex(-1);
          break;
        default:
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, highlightedIndex, filteredOptions]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionsRef.current) {
      const highlightedElement = optionsRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleSelect = (option) => {
    const optionValue = getOptionValue(option);
    onChange({ target: { name, value: optionValue } });
    setIsOpen(false);
    setSearchQuery('');
    setHighlightedIndex(-1);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ target: { name, value: '' } });
    setSearchQuery('');
  };

  const handleToggleOpen = () => {
    if (disabled) return;
    
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchQuery('');
      setHighlightedIndex(-1);
      // Focus the input when opening
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  return (
    <div 
      className={`searchable-select ${className} ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`} 
      ref={dropdownRef}
    >
      <div 
        className="searchable-select-trigger"
        onClick={handleToggleOpen}
      >
        <div className="searchable-select-content">
          {selectedOption ? (
            <span className="searchable-select-value">
              {getOptionLabel(selectedOption)}
            </span>
          ) : (
            <span className="searchable-select-placeholder">{placeholder}</span>
          )}
        </div>
        
        <div className="searchable-select-actions">
          {selectedOption && !disabled && (
            <button
              type="button"
              className="searchable-select-clear"
              onClick={handleClear}
              tabIndex={-1}
            >
              <X size={14} />
            </button>
          )}
          <div className="searchable-select-separator" />
          <ChevronDown 
            size={16} 
            className={`searchable-select-chevron ${isOpen ? 'open' : ''}`}
          />
        </div>
      </div>
      
      {isOpen && !disabled && (
        <div className="searchable-select-dropdown">
          <div className="searchable-select-search">
            <Search size={16} className="searchable-select-search-icon" />
            <input
              ref={inputRef}
              type="text"
              className="searchable-select-search-input"
              placeholder="Type to search..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setHighlightedIndex(-1);
              }}
              autoFocus
            />
          </div>
          
          <div className="searchable-select-options" ref={optionsRef}>
            {loading ? (
              <div className="searchable-select-loading">Loading...</div>
            ) : filteredOptions.length === 0 ? (
              <div className="searchable-select-no-options">{noOptionsText}</div>
            ) : (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  className={`searchable-select-option ${
                    getOptionValue(option) === value ? 'selected' : ''
                  } ${index === highlightedIndex ? 'highlighted' : ''}`}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {getOptionLabel(option)}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;